import { seededRandom } from '#app/utils/motion.ts'
import { venueKey } from './festival-facts.ts'
import { type ProgramEvent } from './program-event.ts'
import { SKYLINE } from './vorra-terrain.generated.ts'

/*
 * The front page's night scene as data: the Vorrå skyline, one house per
 * venue with a window per concert, the stars and the notes rising out of the
 * open door. The hero (HeroScene) and the printed festival poster both draw
 * from here, so the poster is the same night as the site.
 */

/** One venue on the hill: a small house with one window per concert. */
export type SceneVenue = {
	key: string
	/** program order (0-based) of each concert here — windows light up in it */
	concerts: number[]
}

/**
 * The program as houses: one per venue (in order of its first concert), each
 * listing the program order of the concerts it hosts. Events must be sorted
 * by start time.
 */
export function getSceneVenues(
	events: Array<Pick<ProgramEvent, 'id' | 'location' | 'host' | 'date'>>,
): SceneVenue[] {
	const venues = new Map<string, SceneVenue>()
	events.forEach((event, order) => {
		const key = venueKey(event) || `event-${event.id}`
		const venue = venues.get(key) ?? { key, concerts: [] }
		venue.concerts.push(order)
		venues.set(key, venue)
	})
	return [...venues.values()]
}

// The transect runs on into the sea; the scene stops at the last shoulder of
// the Vorrå ridge, so the skyline is all land.
const LAND = (() => {
	let end = SKYLINE.length - 1
	while (end > 0 && SKYLINE[end]! < 0.12) end--
	return SKYLINE.slice(0, end + 1)
})()

/** ground height (0..1) at t (0..1) along the Kartverket skyline transect */
const skylineAt = (t: number) => {
	const f = Math.min(1, Math.max(0, t)) * (LAND.length - 1)
	const i = Math.floor(f)
	const a = LAND[i]!
	const b = LAND[Math.min(i + 1, LAND.length - 1)]!
	return a + (b - a) * (f - i)
}

/**
 * Height of the far hills (0 = foot, 1 = the top) at horizontal position x
 * (0..1). It is the real ground profile from the top of Helgabergsnuten east
 * across Vorrå (Kartverket elevation model). Phones see it from the south —
 * the hill on the left, the Vorrå ridge on the right. Wide screens (and the
 * poster) see it from the north, so the hill rises behind the festival house
 * and the ridge, where the concerts are, runs under the headline. Houses
 * stand on the same curve that draws the hills; the festival house has its
 * own knoll in front.
 */
export const hillProfiles = {
	narrow: (x: number) => skylineAt(x),
	wide: (x: number) => skylineAt(1 - x),
}

/** The hill as a filled path in a 100 × 30 box, ground along the bottom. */
export const hillPath = (profile: (x: number) => number) => {
	const points = Array.from({ length: 121 }, (_, i) => {
		const x = i / 120
		return `${(x * 100).toFixed(2)} ${(30 - 30 * profile(x)).toFixed(2)}`
	})
	return `M0 30 L${points.join(' L')} L100 30 Z`
}

/**
 * Where venue houses stand (0..1 across), clear of the festival house and
 * its knoll (middle on phones, right on wide screens) — mostly along the
 * Vorrå ridge.
 */
export const HOUSE_SLOTS = {
	narrow: [0.05, 0.13, 0.87, 0.95, 0.09, 0.91],
	wide: [0.1, 0.19, 0.28, 0.37, 0.145, 0.325],
}
export const MAX_SCENE_HOUSES = HOUSE_SLOTS.narrow.length

/**
 * A venue house in a box 30 units high: up to three windows side by side
 * (more concerts still light the house), each window 5 × 6 at (x, 16).
 */
export function venueHouseShape(concerts: number) {
	const panes = Math.max(1, Math.min(3, concerts))
	const width = 16 + panes * 8
	return {
		width,
		body: `M1 30V13L${width / 2} 2 ${width - 1} 13V30Z`,
		windows: Array.from({ length: panes }, (_, i) => ({ x: 6 + i * 8, y: 16 })),
	}
}

/**
 * The knoll the festival house stands on, in a 400 × 120 box: broad, with a
 * flat top (y 18) wider than the house, so its walls stand on it everywhere
 * and no corner overhangs a slope.
 */
export const KNOLL =
	'M0 80 C30 79 52 48 76 27 C84 20 92 18 104 18 L296 18 C308 18 316 20 324 27 C348 48 370 79 400 80 Z'

/** Stars in a 100 × 62 sky (drawn with `slice`, so the sky is filled). */
export const SCENE_STARS = (() => {
	const random = seededRandom(1811)
	return Array.from({ length: 46 }, (_, i) => ({
		x: random() * 100,
		y: random() * 62,
		r: 0.6 + random() * 1.1,
		twinkle: i % 3 === 0,
		delay: random() * 6,
	}))
})()

/**
 * Notes drifting up out of the open door: start position in % of the house
 * box, size and drift/rise in px at the hero's 384 px wide house.
 */
export const SCENE_NOTES = (() => {
	const random = seededRandom(2026)
	return Array.from({ length: 11 }, (_, i) => ({
		kind: (i % 3 === 0 ? 'beam' : 'eighth') as 'beam' | 'eighth',
		x: 38 + random() * 34,
		y: 56 + random() * 22,
		size: 14 + random() * 12,
		drift: (random() - 0.5) * 90,
		rise: 150 + random() * 120,
		duration: 7 + random() * 6,
		delay: 1.4 + i * 0.9 + random() * 0.8,
		tilt: (random() - 0.5) * 50,
	}))
})()
